'use client';

import { whitelabelConfig } from '@/brands';
import {
  type CheckoutCheckboxConfig,
  type CheckoutCheckboxesConfig,
  type CheckoutCheckboxLink,
  type TermsCheckboxConfig,
} from '@/brands/types';
import { LegalKey } from '@/types/enums/WhitelabelConfig';
import { parseBoolean } from '@/utils';

import { useWhitelabelConfig } from './useWhitelabelConfig';

type CheckoutCheckboxesConfigOverride = Partial<{
  marketingOptIn: Partial<CheckoutCheckboxConfig>;
  termsOfService: Partial<TermsCheckboxConfig>;
}>;

const mergeCheckoutCheckboxesConfig = (
  override: CheckoutCheckboxesConfigOverride | undefined,
  fallback: CheckoutCheckboxesConfig,
): CheckoutCheckboxesConfig => {
  if (!override || typeof override !== 'object') return fallback;

  const rawLinks = Array.isArray(override.termsOfService?.links)
    ? override.termsOfService.links
    : fallback.termsOfService.links;
  const links = rawLinks.filter((link): link is CheckoutCheckboxLink =>
    Boolean(
      link &&
      typeof link === 'object' &&
      typeof link.text === 'string' &&
      typeof link.href === 'string',
    ),
  );

  return {
    marketingOptIn: {
      visible: parseBoolean(
        override.marketingOptIn?.visible?.toString(),
        fallback.marketingOptIn.visible,
      ),
      required: parseBoolean(
        override.marketingOptIn?.required?.toString(),
        fallback.marketingOptIn.required,
      ),
      label: override.marketingOptIn?.label || fallback.marketingOptIn.label,
      errorMessage:
        override.marketingOptIn?.errorMessage || fallback.marketingOptIn.errorMessage,
    },
    termsOfService: {
      visible: parseBoolean(
        override.termsOfService?.visible?.toString(),
        fallback.termsOfService.visible,
      ),
      required: parseBoolean(
        override.termsOfService?.required?.toString(),
        fallback.termsOfService.required,
      ),
      label: override.termsOfService?.label || fallback.termsOfService.label,
      errorMessage:
        override.termsOfService?.errorMessage || fallback.termsOfService.errorMessage,
      prefixText:
        override.termsOfService?.prefixText || fallback.termsOfService.prefixText,
      brandText: override.termsOfService?.brandText || fallback.termsOfService.brandText,
      links: links.length ? links : fallback.termsOfService.links,
    },
  };
};

export const useRegistrationCheckboxes = (): CheckoutCheckboxesConfig => {
  const { getValue } = useWhitelabelConfig();

  const hasGeneralConditions = Boolean(getValue(LegalKey.LEGAL_GENERAL_CONDITIONS));
  const defaultTermsLinks: CheckoutCheckboxLink[] = [
    { text: 'Política de Privacidade', href: '/politica-de-privacidade' },
    { text: 'Termos de Uso', href: '/termos-de-uso' },
  ];

  if (hasGeneralConditions) {
    defaultTermsLinks.push({ text: 'Condições Gerais', href: '/condicoes-gerais' });
  }

  const fallbackTerms = {
    visible: true,
    required: true,
    label: 'Concordo com os termos',
    errorMessage: 'Você deve concordar com os Termos de Uso',
    prefixText: 'Concordo com a',
    brandText: `do ${whitelabelConfig.displayName}`,
    links: [],
  };
  const wlConfig = whitelabelConfig.checkoutCheckboxes;
  const wlTerms = wlConfig?.termsOfService;

  const defaultCheckoutCheckboxesConfig: CheckoutCheckboxesConfig = {
    marketingOptIn: {
      visible: wlConfig?.marketingOptIn?.visible ?? true,
      required: wlConfig?.marketingOptIn?.required ?? true,
      label:
        wlConfig?.marketingOptIn?.label ||
        'Aceito receber comunicações de marketing e ofertas',
      errorMessage:
        wlConfig?.marketingOptIn?.errorMessage ||
        'Você deve aceitar receber comunicações de marketing',
    },
    termsOfService: {
      visible: wlTerms?.visible ?? fallbackTerms.visible,
      required: wlTerms?.required ?? fallbackTerms.required,
      label: wlTerms?.label || fallbackTerms.label,
      errorMessage: wlTerms?.errorMessage || fallbackTerms.errorMessage,
      prefixText: wlTerms?.prefixText || fallbackTerms.prefixText,
      brandText: wlTerms?.brandText || fallbackTerms.brandText,
      links: wlTerms?.links?.length ? wlTerms.links : defaultTermsLinks,
    },
  };

  const checkoutCheckboxesOverride = getValue<CheckoutCheckboxesConfigOverride>(
    LegalKey.LEGAL_CHECKOUT_CHECKBOXES,
  );

  return mergeCheckoutCheckboxesConfig(
    checkoutCheckboxesOverride,
    defaultCheckoutCheckboxesConfig,
  );
};
